/**
 * @module game/StateMachine
 * Finite state machine for Fruit Slasher.
 *
 * States drive which input systems are active:
 *  - MENU / MODESELECT / GAMEOVER → dwell-click only
 *  - READY                         → neither (camera/hand readiness gate)
 *  - PLAYING                       → slash detection only
 *  - PAUSED                        → dwell-click only
 *
 * Keeping slash and dwell-click mutually exclusive prevents accidental
 * game actions in menus and vice versa.
 *
 * READY sits between mode selection (or retry) and PLAYING: the game must
 * not start until the camera stream + hand tracker are live and a hand has
 * been confirmed in frame — otherwise the player burns lives against an
 * input source that does not exist yet.
 */

/** All valid game states. */
export type GameState =
  | "MENU"
  | "MODESELECT"
  | "READY"
  | "PLAYING"
  | "PAUSED"
  | "GAMEOVER";

/** Listener called whenever the state changes. */
export type StateListener = (next: GameState, prev: GameState) => void;

/** Legal transitions — key: from-state, value: allowed to-states. */
const TRANSITIONS: Record<GameState, readonly GameState[]> = {
  MENU: ["MODESELECT"],
  MODESELECT: ["MENU", "READY"],
  READY: ["PLAYING", "MENU"],
  PLAYING: ["PAUSED", "GAMEOVER"],
  PAUSED: ["PLAYING", "MENU"],
  GAMEOVER: ["MENU", "READY"],
} as const;

/** Thin state machine with guarded transitions and change listeners. */
export class StateMachine {
  private _state: GameState = "MENU";
  private readonly _listeners: StateListener[] = [];

  /** Current game state. */
  get state(): GameState {
    return this._state;
  }

  /**
   * Attempts to transition to `next`.
   * Throws if the transition is not defined in {@link TRANSITIONS}.
   */
  transition(next: GameState): void {
    const allowed = TRANSITIONS[this._state];
    if (!allowed.includes(next)) {
      throw new Error(
        `Invalid transition: ${this._state} → ${next}`,
      );
    }
    const prev = this._state;
    this._state = next;
    this._listeners.forEach((fn) => fn(next, prev));
  }

  /** Registers a callback for state-change events. */
  onTransition(listener: StateListener): () => void {
    this._listeners.push(listener);
    return () => {
      const idx = this._listeners.indexOf(listener);
      if (idx !== -1) this._listeners.splice(idx, 1);
    };
  }
}
